/**
 * OSRM Routing Service
 * 
 * Service for calculating road paths between stops using OSRM (Open Source Routing Machine).
 * Uses backend proxy to avoid CORS issues.
 */

import type { FixedRouteStop } from '../../types/fixedRoutes';
import { getAtlasApiBaseUrl } from '../getAtlasApiBaseUrl';
import { atlasFetch } from '../api/atlasBackendClient';

// Use backend proxy endpoint to avoid CORS issues
const API_BASE_URL = getAtlasApiBaseUrl();

// Timeout for OSRM requests (in milliseconds)
const OSRM_TIMEOUT = 15000; // 15 seconds
const BATCH_PAIR_CACHE_MAX = 50000;
const BATCH_COORD_PRECISION = 6;

type TravelBatchMetric = { travelTimeMinutes: number; distanceMeters: number };
type MetricDeferred = {
  promise: Promise<TravelBatchMetric>;
  resolve: (value: TravelBatchMetric) => void;
  reject: (reason?: unknown) => void;
};
const travelBatchPairCache = new Map<string, TravelBatchMetric>();
const inflightTravelBatchPairCache = new Map<string, Promise<TravelBatchMetric>>();

function toRounded(value: number): string {
  return value.toFixed(BATCH_COORD_PRECISION);
}

function toBatchPairCacheKey(
  profile: string,
  fromLat: number,
  fromLon: number,
  toLat: number,
  toLon: number
): string {
  return `${profile}|${toRounded(fromLat)},${toRounded(fromLon)}->${toRounded(toLat)},${toRounded(toLon)}`;
}

function touchBatchPairCacheEntry(key: string, value: TravelBatchMetric): void {
  if (travelBatchPairCache.has(key)) {
    travelBatchPairCache.delete(key);
  }
  travelBatchPairCache.set(key, value);
  if (travelBatchPairCache.size <= BATCH_PAIR_CACHE_MAX) return;
  const oldestKey = travelBatchPairCache.keys().next().value as string | undefined;
  if (oldestKey) travelBatchPairCache.delete(oldestKey);
}

export function clearTravelBatchCacheForTests(): void {
  travelBatchPairCache.clear();
  inflightTravelBatchPairCache.clear();
}

function createMetricDeferred(): MetricDeferred {
  let resolve!: (value: TravelBatchMetric) => void;
  let reject!: (reason?: unknown) => void;
  const promise = new Promise<TravelBatchMetric>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
}

/**
 * Check if OSRM service is available via backend proxy
 */
async function checkOSRMAvailability(): Promise<boolean> {
  try {
    // Simple test request to check if backend OSRM proxy is reachable
    const testUrl = `${API_BASE_URL}/api/osrm/travel-time`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    const response = await atlasFetch(testUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        from_coords: [0, 0],
        to_coords: [0, 0],
      }),
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    // 400 is expected for invalid coordinates, but means backend is reachable
    return response.status === 200 || response.status === 400;
  } catch (error) {
    return false;
  }
}

/**
 * Get routing profile based on bus routing setting
 * Returns "bus" for bus routes, "driving" otherwise
 */
export function getRoutingProfile(isBusRoute: boolean): string {
  return isBusRoute ? "bus" : "driving";
}

/**
 * Calculate route geometry between two stops using OSRM via backend proxy
 * Returns the full coordinate sequence following roads
 * 
 * @param fromStop - Starting stop
 * @param toStop - Destination stop
 * @param profile - OSRM routing profile (default: "driving")
 */
export async function calculateRouteGeometry(
  fromStop: FixedRouteStop,
  toStop: FixedRouteStop,
  profile: string = "driving"
): Promise<[number, number][] | null> {
  try {
    if (!fromStop.coordinates || !toStop.coordinates) {
      throw new Error('Stops must have coordinates');
    }

    const url = `${API_BASE_URL}/api/osrm/route`;
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), OSRM_TIMEOUT);

    let response: Response;
    try {
      response = await atlasFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          from_coords: [fromStop.coordinates.lat, fromStop.coordinates.lng],
          to_coords: [toStop.coordinates.lat, toStop.coordinates.lng],
          profile: profile,
        }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
    } catch (fetchError) {
      clearTimeout(timeoutId);
      
      // Handle network errors gracefully
      if (fetchError instanceof Error) {
        if (fetchError.name === 'AbortError') {
          console.warn('[OSRM] Request timed out, using fallback');
          return null;
        }
      }
      
      console.error('[OSRM] Network error:', fetchError);
      return null;
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[OSRM] Route geometry API error response:', errorText);
      
      // Handle specific error cases
      if (response.status === 404) {
        // Profile might not be available
        if (profile === 'bus') {
          throw new Error('Bus routing profile is not available on the OSRM server. Please ensure the OSRM server is configured with the bus profile. See docs/OSRM_BUS_SETUP.md for setup instructions.');
        }
        throw new Error(`OSRM profile '${profile}' not found. The routing service may not support this profile.`);
      } else if (response.status === 429) {
        // Rate limiting - retry with exponential backoff
        throw new Error('OSRM rate limit exceeded. Please wait and try again.');
      } else if (response.status === 503) {
        throw new Error('OSRM routing service is temporarily unavailable. Please try again later.');
      } else if (response.status === 500) {
        throw new Error('OSRM server error. The routing service may be experiencing issues.');
      }
      
      throw new Error(`OSRM API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    // Backend returns geometry as [lat, lng] coordinates
    const coordinatesSequence: [number, number][] = data.geometry || [];

    return coordinatesSequence.length > 0 ? coordinatesSequence : null;
  } catch (error) {
    console.error('OSRM route calculation error:', error);
    return null;
  }
}

/**
 * Calculate travel time and distance between two stops using OSRM
 * Returns time in minutes and distance in meters
 * 
 * @param fromStop - Starting stop
 * @param toStop - Destination stop
 * @param profile - OSRM routing profile (default: "driving")
 */
export async function calculateTravelTimeAndDistance(
  fromStop: FixedRouteStop,
  toStop: FixedRouteStop,
  profile: string = "driving"
): Promise<{ travelTimeMinutes: number; distanceMeters: number }> {
  console.log('[OSRM] calculateTravelTimeAndDistance called', { fromStop: fromStop.name, toStop: toStop.name });
  
  try {
    if (!fromStop.coordinates || !toStop.coordinates) {
      console.error('[OSRM] Missing coordinates', { fromStop, toStop });
      throw new Error('Stops must have coordinates');
    }

    const url = `${API_BASE_URL}/api/osrm/travel-time`;

    console.log('[OSRM] Making request to backend proxy:', url);

    // Add timeout to fetch request
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), OSRM_TIMEOUT);

    let response: Response;
    try {
      response = await atlasFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          from_coords: [fromStop.coordinates.lat, fromStop.coordinates.lng],
          to_coords: [toStop.coordinates.lat, toStop.coordinates.lng],
          profile: profile,
        }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
    } catch (fetchError) {
      clearTimeout(timeoutId);
      
      // Handle network errors (connection refused, timeout, etc.)
      if (fetchError instanceof Error) {
        if (fetchError.name === 'AbortError') {
          throw new Error('OSRM request timed out. The routing service may be unavailable or slow. Please try again later.');
        }
        if (fetchError.message.includes('Failed to fetch') || fetchError.message.includes('NetworkError')) {
          throw new Error('Unable to connect to routing service. Please check your internet connection and ensure the backend server is running.');
        }
      }
      
      throw new Error(`Network error: ${fetchError instanceof Error ? fetchError.message : 'Unknown error'}`);
    }

    console.log('[OSRM] Response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[OSRM] Route geometry API error response:', errorText);
      
      // Handle specific error cases
      if (response.status === 404) {
        // Profile might not be available
        if (profile === 'bus') {
          throw new Error('Bus routing profile is not available on the OSRM server. Please ensure the OSRM server is configured with the bus profile. See docs/OSRM_BUS_SETUP.md for setup instructions.');
        }
        throw new Error(`OSRM profile '${profile}' not found. The routing service may not support this profile.`);
      } else if (response.status === 429) {
        // Rate limiting - retry with exponential backoff
        throw new Error('OSRM rate limit exceeded. Please wait and try again.');
      } else if (response.status === 503) {
        throw new Error('OSRM routing service is temporarily unavailable. Please try again later.');
      } else if (response.status === 500) {
        throw new Error('OSRM server error. The routing service may be experiencing issues.');
      }
      
      throw new Error(`OSRM API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('[OSRM] Response data:', { duration_seconds: data.duration_seconds, distance_meters: data.distance_meters });

    // Backend returns duration in seconds
    // Convert to minutes and round
    const durationSeconds = data.duration_seconds;
    if (!durationSeconds || isNaN(durationSeconds)) {
      console.error('[OSRM] Invalid duration', durationSeconds);
      throw new Error('Invalid duration returned from OSRM');
    }
    const durationMinutes = Math.round(durationSeconds / 60);
    
    // Get distance in meters
    const distanceMeters = data.distance_meters || 0;
    if (distanceMeters && isNaN(distanceMeters)) {
      console.warn('[OSRM] Invalid distance', distanceMeters);
    }
    
    console.log('[OSRM] Calculated travel time:', durationMinutes, 'minutes (', durationSeconds, 'seconds), distance:', distanceMeters, 'meters');

    return {
      travelTimeMinutes: durationMinutes,
      distanceMeters: distanceMeters || 0,
    };
  } catch (error) {
    console.error('[OSRM] Travel time and distance calculation error:', error);
    throw error;
  }
}

/**
 * Calculate travel times and distances for multiple segments in a single batch request
 * Uses OSRM Table service for efficient batch processing
 * 
 * @param segments - Array of stop pairs to calculate
 * @param profile - OSRM routing profile (default: "driving")
 * @param externalSignal - When aborted, cancels the in-flight request (in addition to the batch timeout).
 */
export async function calculateTravelTimesAndDistancesBatch(
  segments: Array<{ fromStop: FixedRouteStop; toStop: FixedRouteStop }>,
  profile: string = "driving",
  externalSignal?: AbortSignal
): Promise<Array<{ travelTimeMinutes: number; distanceMeters: number }>> {
  console.log('[OSRM] calculateTravelTimesAndDistancesBatch called', { segmentCount: segments.length });
  let activeDeferredByKey: Map<string, MetricDeferred> | null = null;
  
  try {
    if (segments.length === 0) {
      return [];
    }

    // Validate all stops have coordinates
    for (const segment of segments) {
      if (!segment.fromStop.coordinates || !segment.toStop.coordinates) {
        throw new Error('All stops must have coordinates');
      }
    }

    // Resolve from cache and collect unresolved pairs for one backend request.
    const results: Array<TravelBatchMetric | null> = Array(segments.length).fill(null);
    const unresolved: Array<{ idx: number; key: string; from: { lat: number; lng: number }; to: { lat: number; lng: number } }> = [];
    const inflightAwaiters: Array<Promise<void>> = [];
    for (let idx = 0; idx < segments.length; idx += 1) {
      const segment = segments[idx]!;
      const from = segment.fromStop.coordinates!;
      const to = segment.toStop.coordinates!;
      const cacheKey = toBatchPairCacheKey(profile, from.lat, from.lng, to.lat, to.lng);
      const cached = travelBatchPairCache.get(cacheKey);
      if (cached) {
        results[idx] = cached;
        touchBatchPairCacheEntry(cacheKey, cached);
        continue;
      }
      const inflight = inflightTravelBatchPairCache.get(cacheKey);
      if (inflight) {
        inflightAwaiters.push(
          inflight.then((metric) => {
            results[idx] = metric;
            touchBatchPairCacheEntry(cacheKey, metric);
          })
        );
        continue;
      }
      unresolved.push({ idx, key: cacheKey, from, to });
    }
    if (inflightAwaiters.length > 0) {
      await Promise.all(inflightAwaiters);
    }
    if (unresolved.length === 0) {
      return results.map((result) => result ?? { travelTimeMinutes: 0, distanceMeters: 0 });
    }

    // Deduplicate unresolved segment pairs within this batch call.
    const unresolvedByKey = new Map<string, { from: { lat: number; lng: number }; to: { lat: number; lng: number }; requestIndices: number[] }>();
    for (let i = 0; i < unresolved.length; i += 1) {
      const entry = unresolved[i]!;
      const existing = unresolvedByKey.get(entry.key);
      if (existing) {
        existing.requestIndices.push(entry.idx);
      } else {
        unresolvedByKey.set(entry.key, { from: entry.from, to: entry.to, requestIndices: [entry.idx] });
      }
    }
    const unresolvedUnique = Array.from(unresolvedByKey.entries());

    const deferredByKey = new Map<string, MetricDeferred>();
    activeDeferredByKey = deferredByKey;
    for (let i = 0; i < unresolvedUnique.length; i += 1) {
      const [cacheKey] = unresolvedUnique[i]!;
      const deferred = createMetricDeferred();
      deferredByKey.set(cacheKey, deferred);
      inflightTravelBatchPairCache.set(cacheKey, deferred.promise);
      void deferred.promise.finally(() => {
        inflightTravelBatchPairCache.delete(cacheKey);
      });
    }

    // Collect all unique coordinates
    const coordinateMap = new Map<string, number>(); // coord string -> index
    const coordinates: [number, number][] = [];
    const pairs: [number, number][] = [];

    unresolvedUnique.forEach(([, unresolvedEntry]) => {
      const fromCoord = `${unresolvedEntry.from.lat},${unresolvedEntry.from.lng}`;
      const toCoord = `${unresolvedEntry.to.lat},${unresolvedEntry.to.lng}`;

      // Add from coordinate if not seen
      if (!coordinateMap.has(fromCoord)) {
        coordinateMap.set(fromCoord, coordinates.length);
        coordinates.push([unresolvedEntry.from.lat, unresolvedEntry.from.lng]);
      }

      // Add to coordinate if not seen
      if (!coordinateMap.has(toCoord)) {
        coordinateMap.set(toCoord, coordinates.length);
        coordinates.push([unresolvedEntry.to.lat, unresolvedEntry.to.lng]);
      }

      // Record pair indices
      const fromIdx = coordinateMap.get(fromCoord)!;
      const toIdx = coordinateMap.get(toCoord)!;
      pairs.push([fromIdx, toIdx]);
    });

    console.log('[OSRM] Batch request:', {
      uniqueCoordinates: coordinates.length,
      pairs: pairs.length,
      requested: segments.length,
      unresolved: unresolved.length,
      uniqueUnresolved: unresolvedUnique.length,
    });

    const url = `${API_BASE_URL}/api/osrm/travel-time-batch`;

    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), OSRM_TIMEOUT * 2); // Allow more time for batch requests

    const onExternalAbort = () => {
      clearTimeout(timeoutId);
      timeoutController.abort();
    };
    if (externalSignal) {
      if (externalSignal.aborted) {
        clearTimeout(timeoutId);
        throw new DOMException('Aborted', 'AbortError');
      }
      externalSignal.addEventListener('abort', onExternalAbort, { once: true });
    }

    let response: Response;
    try {
      response = await atlasFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          coordinates: coordinates,
          pairs: pairs,
          profile: profile,
        }),
        signal: timeoutController.signal,
      });
      clearTimeout(timeoutId);
    } catch (fetchError) {
      clearTimeout(timeoutId);

      if (fetchError instanceof DOMException && fetchError.name === 'AbortError') {
        throw fetchError;
      }

      if (fetchError instanceof Error) {
        if (fetchError.name === 'AbortError') {
          throw new Error('OSRM batch request timed out. The routing service may be unavailable or slow.');
        }
        if (fetchError.message.includes('Failed to fetch') || fetchError.message.includes('NetworkError')) {
          throw new Error('Unable to connect to routing service. Please check your internet connection and ensure the backend server is running.');
        }
      }
      
      throw new Error(`Network error: ${fetchError instanceof Error ? fetchError.message : 'Unknown error'}`);
    } finally {
      if (externalSignal) {
        externalSignal.removeEventListener('abort', onExternalAbort);
      }
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[OSRM] Batch API error response:', errorText);
      
      // Handle specific error cases
      if (response.status === 404) {
        // Profile might not be available
        if (profile === 'bus') {
          throw new Error('Bus routing profile is not available on the OSRM server. Please ensure the OSRM server is configured with the bus profile. See docs/OSRM_BUS_SETUP.md for setup instructions.');
        }
        throw new Error(`OSRM profile '${profile}' not found. The routing service may not support this profile.`);
      } else if (response.status === 429) {
        // Rate limiting - retry with exponential backoff
        throw new Error('OSRM rate limit exceeded. Please wait and try again.');
      } else if (response.status === 503) {
        throw new Error('OSRM routing service is temporarily unavailable. Please try again later.');
      } else if (response.status === 500) {
        throw new Error('OSRM server error. The routing service may be experiencing issues.');
      }
      
      throw new Error(`OSRM API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('[OSRM] Batch response received:', { resultCount: data.results?.length });

    // Convert backend response and populate caches/results.
    const responseMetrics: TravelBatchMetric[] = data.results.map(
      (result: { duration_seconds: number; distance_meters: number }) => ({
      travelTimeMinutes: Math.round(result.duration_seconds / 60),
      distanceMeters: result.distance_meters || 0,
      })
    );
    for (let i = 0; i < unresolvedUnique.length; i += 1) {
      const [cacheKey, unresolvedEntry] = unresolvedUnique[i]!;
      const metric = responseMetrics[i] ?? { travelTimeMinutes: 0, distanceMeters: 0 };
      touchBatchPairCacheEntry(cacheKey, metric);
      for (let j = 0; j < unresolvedEntry.requestIndices.length; j += 1) {
        const resultIdx = unresolvedEntry.requestIndices[j]!;
        results[resultIdx] = metric;
      }
      deferredByKey.get(cacheKey)?.resolve(metric);
    }

    return results.map((result) => result ?? { travelTimeMinutes: 0, distanceMeters: 0 });
  } catch (error) {
    if (activeDeferredByKey) {
      activeDeferredByKey.forEach((deferred) => deferred.reject(error));
      activeDeferredByKey = null;
    }
    console.error('[OSRM] Batch travel time and distance calculation error:', error);
    throw error;
  }
}

/**
 * Calculate route geometry for a complete route with multiple stops
 * Returns an array of coordinate sequences for each segment
 * 
 * @param stops - Array of stops in route order
 * @param profile - OSRM routing profile (default: "driving")
 */
export async function calculateRouteGeometryForStops(
  stops: FixedRouteStop[],
  profile: string = "driving"
): Promise<[number, number][][]> {
  const segments: [number, number][][] = [];

  for (let i = 0; i < stops.length - 1; i++) {
    const fromStop = stops[i];
    const toStop = stops[i + 1];

    try {
      const geometry = await calculateRouteGeometry(fromStop, toStop, profile);
      if (geometry && geometry.length > 0) {
        segments.push(geometry);
      } else {
        // Don't create fallback - throw error instead to prevent straight-line routes
        throw new Error(`Failed to calculate route geometry between stops ${i} and ${i + 1}`);
      }

      // Minimal rate limiting: wait 100ms between requests (reduced from 1100ms for faster rendering)
      // This still prevents overwhelming the server while maintaining responsiveness
      if (i < stops.length - 2) { // Don't delay after last segment
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    } catch (error) {
      console.error(`Failed to calculate route segment ${i} to ${i + 1}:`, error);
      
      // If it's a rate limit error (429), wait longer and retry once
      if (error instanceof Error && error.message.includes('rate limit')) {
        console.warn(`[OSRM] Rate limit hit, waiting 2 seconds before retry...`);
        await new Promise((resolve) => setTimeout(resolve, 2000));
        
        try {
          const retryGeometry = await calculateRouteGeometry(fromStop, toStop, profile);
          if (retryGeometry && retryGeometry.length > 0) {
            segments.push(retryGeometry);
            console.log(`[OSRM] Retry successful for segment ${i} to ${i + 1}`);
            continue;
          }
        } catch (retryError) {
          console.error(`[OSRM] Retry also failed for segment ${i} to ${i + 1}:`, retryError);
        }
      }
      
      // Don't create fallback - rethrow to prevent straight-line routes
      throw error;
    }
  }

  return segments;
}

